"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ItemImage, CategorySelection, DayOfWeek } from "@/lib/types";
import { categoryLabels } from "@/lib/categoryLabels";
import {
  categoryStructure as defaultCategoryStructure,
  type CategoryStructure,
} from "@/lib/categoryStructure";
import { activeSubcategoryIdSet } from "@/lib/activeCategories";
import { CategoryDropdown } from "@/components/CategoryDropdown";
import { CategoryAutoDetector } from "@/components/CategoryAutoDetector";
import BrandSelector from "@/components/listing/BrandSelector";
import SizeInput from "@/components/listing/SizeInput";
import ModelInput from "@/components/listing/ModelInput";
import PriceInput from "@/components/listing/PriceInput";
import ItemImageManager from "@/components/ItemImageManager";
import SellLocationField from "@/components/listing/SellLocationField";
import type { HierarchicalLocationOnChange } from "@/lib/locationIds";
import type { SellProfileLocation } from "@/lib/listing/listingLocationIds";
import PickupScheduleFields from "@/components/listing/PickupScheduleFields";
import Input from "@/components/ui/Input";
import FieldError from "@/components/ui/FieldError";
import { getCategoryFieldConfig } from "@/lib/listing/fieldConfigs";
import { sellListingShowsModelField } from "@/lib/listing/listingIdentityPolicy";
import FormStep from "./FormStep";
import StepNavigation from "./StepNavigation";
import EnhancedTitleInput from "./EnhancedTitleInput";
import FormProgressBar from "./FormProgressBar";
import SubcategoryRulesCard from "./SubcategoryRulesCard";
import SellCategoryAvailabilityNotice from "./SellCategoryAvailabilityNotice";
import ChairSofaCategoryNudge from "./ChairSofaCategoryNudge";
import DescriptionContactPolicyNotice from "./DescriptionContactPolicyNotice";
import {
  LISTING_DEFECTS_PLACEHOLDER,
  LISTING_DESCRIPTION_PLACEHOLDER,
  LISTING_TITLE_POLICY_HINT,
} from "@/lib/listing/listingFreeTextPolicy";
import type { SubcategoryListingRule } from "@/lib/subcategoryRules";
import type { SellPriceBandSnapshot } from "@/lib/hooks/useSellPriceBand";
import {
  getListingSpecFormValidationMessage,
  listingSpecPluginHidesBrandLine,
  listingSpecPluginHidesModelLine,
  listingSpecPluginHidesSizeLine,
  resolveListingSpecStepTwoUi,
} from "@/lib/listing/listingSpecPlugin";
import { getStepTwoSizePresentation } from "@/lib/listing/listingSpecSellPresentation";
import ListingFamilySpecFieldsHost from "@/components/listing/ListingFamilySpecFieldsHost";
import type { SellListingFormData } from "@/lib/listing/sellListingFormTypes";
import type { SellListingFormErrors } from "@/lib/listing/sellListingValidation";

interface StepBasedFormProps {
  formData: SellListingFormData;
  errors: SellListingFormErrors;
  uploadedImages: ItemImage[];
  pickupDays: DayOfWeek[];
  onFormDataChange: (data: SellListingFormData) => void;
  onErrorsChange: (errors: SellListingFormErrors) => void;
  onImagesChange: (images: ItemImage[]) => void;
  onPickupDaysChange: (days: DayOfWeek[]) => void;
  onFieldChange: (name: string, value: unknown) => void;
  /** Free-text fields (title/description/defects): validate on blur, not as-you-type. */
  onFieldBlur?: (name: string, value: unknown) => void;
  onLocationChange: HierarchicalLocationOnChange;
  onCategorySelect: (selection: CategorySelection) => void;
  onDayToggle: (day: DayOfWeek) => void;
  validateField: (name: string, value: unknown) => string | undefined;
  onSubmit: () => void;
  isLoading?: boolean;
  sellerCategoryDetectionEnabled: boolean;
  profileLocation?: SellProfileLocation | null;
  /** @deprecated Prefer `profileLocation`. */
  userLocation?: string;
  onStepChange?: (step: number) => void; // Callback to notify parent of step changes
  /** When provided, only these categories/subcategories are shown (e.g. active for sale). */
  categoryStructure?: CategoryStructure[] | null;
  /** Rule for the currently selected subcategory; when set, shown in step 1 and acknowledgement required to proceed. */
  subcategoryRule?: SubcategoryListingRule | null;
  rulesAcknowledged?: boolean;
  onRulesAcknowledgedChange?: (value: boolean) => void;
  priceBandEnforcementActive?: boolean;
  sellPriceBand?: SellPriceBandSnapshot | null;
  sellPriceBandLoading?: boolean;
  sellPriceBandError?: string | null;
  /** Title used for band API (composed listing title). */
  bandTitle?: string;
  /** Structured attrs for attribute-first cohort (e.g. sewing_machine_type). */
  listingAttributes?: Record<string, unknown> | null;
  bandRecommendationChipsEnabled?: boolean;
  /** Dev toolbox: jump to a specific step on mount (development previews only). */
  initialStep?: number;
  devImageValidationPreview?: "warn" | "block" | null;
}

const TOTAL_STEPS = 6;

export default function StepBasedForm({
  formData,
  errors,
  uploadedImages,
  pickupDays,
  onFormDataChange: _onFormDataChange,
  onErrorsChange,
  onImagesChange,
  onPickupDaysChange: _onPickupDaysChange,
  onFieldChange,
  onFieldBlur,
  onLocationChange,
  onCategorySelect,
  onDayToggle,
  validateField,
  onSubmit,
  isLoading = false,
  sellerCategoryDetectionEnabled,
  profileLocation,
  userLocation,
  onStepChange,
  categoryStructure: categoryStructureProp,
  subcategoryRule = null,
  rulesAcknowledged = false,
  onRulesAcknowledgedChange,
  priceBandEnforcementActive = false,
  sellPriceBand = null,
  sellPriceBandLoading = false,
  sellPriceBandError = null,
  bandTitle,
  listingAttributes,
  bandRecommendationChipsEnabled = false,
  initialStep,
  devImageValidationPreview = null,
}: StepBasedFormProps) {
  const [currentStep, setCurrentStep] = useState(() => {
    if (
      initialStep &&
      initialStep >= 1 &&
      initialStep <= TOTAL_STEPS &&
      process.env.NODE_ENV === "development"
    ) {
      return initialStep;
    }
    return 1;
  });
  const appliedDevInitialStepRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (!initialStep || initialStep < 1 || initialStep > TOTAL_STEPS) {
      return;
    }
    if (appliedDevInitialStepRef.current === initialStep) {
      return;
    }
    appliedDevInitialStepRef.current = initialStep;
    const timer = window.setTimeout(() => {
      setCurrentStep(initialStep);
      onStepChange?.(initialStep);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [initialStep, onStepChange]);

  const categoryStructure =
    categoryStructureProp && categoryStructureProp.length > 0
      ? categoryStructureProp
      : defaultCategoryStructure;

  const activeSubcategoryIds = useMemo(
    () => activeSubcategoryIdSet(categoryStructure),
    [categoryStructure],
  );

  const stepTwoSpecError = useMemo(() => {
    if (!formData.subcategory) return undefined;
    return getListingSpecFormValidationMessage({
      subcategoryId: formData.subcategory,
      sizeText: formData.size,
      familySpecs: formData.familySpecs,
      activeSubcategoryIds,
    });
  }, [
    formData.subcategory,
    formData.size,
    formData.familySpecs,
    activeSubcategoryIds,
  ]);

  const stepTwoSizePresentation = useMemo(() => {
    const cfg = getCategoryFieldConfig(formData.category);
    if (!cfg?.size.enabled) return null;
    return getStepTwoSizePresentation({
      category: formData.category,
      subcategoryId: formData.subcategory,
      activeSubcategoryIds,
      fallback: cfg.size,
    });
  }, [formData.category, formData.subcategory, activeSubcategoryIds]);

  // Notify parent of step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  // Validate a step
  const validateStep = useCallback(
    (step: number): boolean => {
      switch (step) {
        case 1: // Category + Brand
          // Category is required, brand is optional
          const categoryValid = !validateField("category", formData.category);
          if (!categoryValid) return false;
          // When subcategory has rules, seller must acknowledge before proceeding
          if (
            subcategoryRule &&
            onRulesAcknowledgedChange &&
            !rulesAcknowledged
          )
            return false;
          return true;
        case 2: // Size + Model
          return !stepTwoSpecError;
        case 3: // Title
          return !validateField("title", formData.title);
        case 4: // Price + Location
          return (
            !validateField("price", formData.price) &&
            !validateField("location", formData.location)
          );
        case 5: // Images
          return !validateField("images", uploadedImages);
        case 6: // Description + Defects + Pickup Details
          // Free-text policy is blur + click; keep Continue clickable so we can surface errors.
          return true;
        default:
          return false;
      }
    },
    [
      formData,
      uploadedImages,
      validateField,
      subcategoryRule,
      rulesAcknowledged,
      onRulesAcknowledgedChange,
      stepTwoSpecError,
    ],
  );

  // Check if current step is valid
  const canProceed = validateStep(currentStep);

  const surfaceFreeTextStepErrors = () => {
    if (currentStep === 3) {
      const titleError = validateField("title", formData.title);
      onErrorsChange({ ...errors, title: titleError });
      return !titleError;
    }
    if (currentStep === 6) {
      const descriptionError = validateField(
        "description",
        formData.description,
      );
      const defectsError = validateField("defects", formData.defects);
      onErrorsChange({
        ...errors,
        description: descriptionError,
        defects: defectsError,
      });
      return !descriptionError && !defectsError;
    }
    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (!surfaceFreeTextStepErrors()) return;
    if (canProceed && currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
      // Scroll to top of form
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 80, behavior: "smooth" });
      }
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 80, behavior: "smooth" });
      }
    }
  };

  // Auto-advance when step becomes valid (disabled for now - can be enabled if needed)
  // useEffect(() => {
  // if (canProceed && currentStep < TOTAL_STEPS) {
  // // Auto-advance after 500ms delay for better UX
  // const timer = setTimeout(() => {
  // handleNext();
  // }, 500);
  // return () => clearTimeout(timer);
  // }
  // }, [canProceed, currentStep, handleNext]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Category + Brand
        const showPreSelectionNotices = !formData.subcategory;

        return (
          <div className="grid grid-cols-1 md:grid-cols-1 sm:grid-cols-1 gap-6">
            <div>
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700/50 dark:bg-amber-950/40">
                <p className="text-sm text-amber-900 dark:text-amber-100">
                  <span className="font-semibold">Please, note:</span> Items
                  intentionally listed under the wrong category will be deleted
                  without question.
                </p>
              </div>
              {showPreSelectionNotices ? (
                <SellCategoryAvailabilityNotice
                  categoryStructure={categoryStructure}
                />
              ) : null}
              <label className="block mt-4 mb-2 text-sm font-medium text-foreground">
                Category <span className="text-red-500">*</span>
              </label>
              <CategoryDropdown
                onSelect={onCategorySelect}
                name="category"
                placeholder="Select a category"
                className="w-full"
                categoryStructure={categoryStructure}
                initialValue={
                  formData.category
                    ? {
                        categoryId: formData.category,
                        subcategoryId: formData.subcategory || "",
                        categoryName:
                          categoryLabels[formData.category] ||
                          formData.category,
                        subcategoryName:
                          categoryStructure
                            .find((cat) => cat.id === formData.category)
                            ?.subcategories.find(
                              (sub) => sub.id === formData.subcategory,
                            )?.name ||
                          formData.subcategory ||
                          "",
                      }
                    : undefined
                }
              />
              <FieldError message={errors.category} />
              <ChairSofaCategoryNudge
                title={formData.title}
                subcategoryId={formData.subcategory}
                categoryStructure={
                  categoryStructure && categoryStructure.length > 0
                    ? categoryStructure
                    : defaultCategoryStructure
                }
                onSwitchToSofa={onCategorySelect}
              />
              {formData.subcategory && subcategoryRule && (
                <SubcategoryRulesCard
                  subcategoryName={
                    categoryStructure
                      .find((c) => c.id === formData.category)
                      ?.subcategories.find((s) => s.id === formData.subcategory)
                      ?.name ?? formData.subcategory
                  }
                  rule={subcategoryRule}
                  acknowledged={rulesAcknowledged}
                  onAcknowledgedChange={onRulesAcknowledgedChange}
                />
              )}
              {sellerCategoryDetectionEnabled &&
                formData.title &&
                !formData.category && (
                  <div className="mt-2">
                    <CategoryAutoDetector
                      title={formData.title}
                      onDetect={(category, subcategory) => {
                        if (category) {
                          const categoryData = categoryStructure.find(
                            (cat) => cat.id === category,
                          );
                          if (categoryData) {
                            const subcategoryData =
                              categoryData?.subcategories.find(
                                (sub) => sub.id === subcategory,
                              );
                            if (subcategoryData) {
                              onCategorySelect({
                                categoryId: category,
                                subcategoryId: subcategory,
                                categoryName:
                                  categoryLabels[category] || category,
                                subcategoryName: subcategoryData.name,
                              });
                            }
                          }
                        }
                      }}
                      minWords={3}
                      debounceMs={1000}
                      showLoadingIndicator={true}
                    />
                  </div>
                )}
            </div>
            {formData.category &&
              getCategoryFieldConfig(formData.category)?.brand &&
              !listingSpecPluginHidesBrandLine(formData.subcategory) && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-foreground">
                    Brand{" "}
                    <span className="text-text-light text-xs">(optional)</span>
                  </label>
                  <BrandSelector
                    value={formData.brand}
                    onChange={(brand) => onFieldChange("brand", brand)}
                    category={formData.category}
                    placeholder="Search and choose a brand"
                    className="w-full"
                  />
                  <FieldError message={errors.brand} />
                  <p className="mt-1 text-xs text-text-light">
                    Optional — skip if you don&apos;t see your brand
                  </p>
                </div>
              )}
          </div>
        );

      case 2: // Size (+ optional structured specs). Model deferred — see listingIdentityPolicy.
        const config = getCategoryFieldConfig(formData.category);
        const stepTwoUi = resolveListingSpecStepTwoUi(formData.subcategory);
        const showSize =
          Boolean(config?.size.enabled) &&
          !listingSpecPluginHidesSizeLine(formData.subcategory);
        const showModel =
          sellListingShowsModelField(Boolean(config?.model)) &&
          !listingSpecPluginHidesModelLine(formData.subcategory);

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ListingFamilySpecFieldsHost
              subcategoryId={formData.subcategory}
              familySpecs={formData.familySpecs}
              onChange={(familySpecs) =>
                onFieldChange("familySpecs", familySpecs)
              }
              error={stepTwoSpecError}
            />
            {showSize && config && (
              <div>
                <label className="block mb-2 text-sm font-medium text-foreground">
                  {stepTwoSizePresentation?.fieldLabel ??
                    config.size.label ??
                    "Size"}{" "}
                  {stepTwoSizePresentation?.showRequiredMarker ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-text-light text-xs">(optional)</span>
                  )}
                </label>
                <SizeInput
                  value={formData.size}
                  onChange={(size) => onFieldChange("size", size)}
                  config={
                    stepTwoSizePresentation?.sizeInputConfig ?? config.size
                  }
                  helperTextOverride={
                    stepTwoSizePresentation?.helperTextOverride ?? null
                  }
                  className="w-full"
                />
                <p className="mt-1 text-xs text-text-light">
                  Helps create a more descriptive title
                </p>
                <FieldError message={stepTwoSpecError} />
              </div>
            )}
            {showModel && (
              <div>
                <label className="block mb-2 text-sm font-medium text-foreground">
                  Model{" "}
                  <span className="text-text-light text-xs">(optional)</span>
                </label>
                <ModelInput
                  value={formData.model}
                  onChange={(model) => onFieldChange("model", model)}
                  brand={formData.brand || null}
                  subcategory={formData.subcategory}
                  placeholder="Enter model name (optional)"
                  className="w-full"
                />
                <p className="mt-1 text-xs text-text-light">
                  {formData.brand
                    ? `Model suggestions for ${formData.brand} will appear as you type.`
                    : "Enter model name if you know it"}
                </p>
              </div>
            )}
            {!showSize && !showModel && stepTwoUi.kind === "size_line" && (
              <div className="col-span-2 text-sm text-text-light">
                No additional details required for this category.
              </div>
            )}
            {!showSize &&
              stepTwoUi.kind === "size_line" &&
              stepTwoSpecError && (
                <div className="col-span-2">
                  <FieldError message={stepTwoSpecError} />
                </div>
              )}
          </div>
        );

      case 3: // Title Review
        return (
          <div>
            <label className="block mb-2 text-sm font-medium text-foreground">
              Item Title <span className="text-red-500">*</span>
            </label>
            <EnhancedTitleInput
              brand={formData.brand}
              size={formData.size}
              model={formData.model}
              subcategoryId={formData.subcategory}
              category={formData.category}
              familySpecs={formData.familySpecs}
              title={formData.title}
              onTitleChange={(title) => onFieldChange("title", title)}
              error={errors.title}
              placeholder="What should we call this item?"
            />
            <FieldError message={errors.title} />
            <ChairSofaCategoryNudge
              title={formData.title}
              subcategoryId={formData.subcategory}
              categoryStructure={
                categoryStructure && categoryStructure.length > 0
                  ? categoryStructure
                  : defaultCategoryStructure
              }
              onSwitchToSofa={onCategorySelect}
            />
            <p className="mt-2 text-xs text-text-light">
              Your title is generated from the details you provided and cannot
              be changed, so it always matches what buyers see in search.{" "}
              {LISTING_TITLE_POLICY_HINT}
            </p>
          </div>
        );

      case 4: // Price + Location + Quantity
        return (
          <div className="space-y-6">
            <PriceInput
              stackedLayout
              value={formData.price}
              onChange={(e) => onFieldChange("price", e.target.value)}
              error={errors.price}
              itemTitle={bandTitle ?? formData.title}
              itemCategory={formData.category}
              itemType={formData.subcategory || ""}
              onSuggestedPrice={(price) => {
                onFieldChange("price", price.toLocaleString("en-NG"));
              }}
              bandRecommendationChipsEnabled={bandRecommendationChipsEnabled}
              onClearPrice={() => onFieldChange("price", "")}
              priceBandEnforcementActive={priceBandEnforcementActive}
              sellPriceBand={sellPriceBand}
              sellPriceBandLoading={sellPriceBandLoading}
              sellPriceBandError={sellPriceBandError}
              listingAttributes={listingAttributes}
              secondaryField={
                <div>
                  <label className="block mb-2 text-sm font-medium text-foreground">
                    Quantity
                  </label>
                  <Input
                    name="quantity"
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue =
                        value === ""
                          ? 1
                          : Math.max(1, parseInt(value, 10) || 1);
                      onFieldChange("quantity", numValue);
                    }}
                    className="w-full"
                  />
                  <p className="mt-1 text-xs text-text-light">
                    How many units do you have?
                  </p>
                </div>
              }
            />
            <SellLocationField
              value={formData.location}
              onChange={onLocationChange}
              error={errors.location}
              profileLocation={profileLocation}
              userLocation={userLocation}
            />
          </div>
        );

      case 5: // Images
        return (
          <div>
            {/* <label className="block mb-2 text-sm font-medium text-foreground">
 Item Images <span className="text-red-500">*</span>
 </label>
 <p className="text-sm text-text-light mb-4">
 Upload exactly 3 sharp, well-lit photos. The star marks your main
 image — it appears first in search.
 </p> */}
            <ItemImageManager
              images={uploadedImages}
              onChange={onImagesChange}
              maxImages={3}
              minImages={3}
              forcePermission={true}
              previewMode
              devImageValidationPreview={devImageValidationPreview}
            />
            <FieldError message={errors.images} />
          </div>
        );

      case 6: // Description + Defects + Pickup Details
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> All information on this step is optional,
                but please provide it if available. This helps buyers make
                informed decisions and coordinate pickup times.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-2 text-sm font-medium text-foreground">
                  Description{" "}
                  <span className="text-text-light text-xs">(optional)</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={(e) => onFieldChange("description", e.target.value)}
                  onBlur={(e) => onFieldBlur?.("description", e.target.value)}
                  placeholder={LISTING_DESCRIPTION_PLACEHOLDER}
                  className="w-full p-2 border rounded-lg min-h-[120px]"
                  rows={4}
                />
                <FieldError message={errors.description} />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-foreground">
                  Defects{" "}
                  <span className="text-text-light text-xs">(optional)</span>
                </label>
                <textarea
                  name="defects"
                  value={formData.defects}
                  onChange={(e) => onFieldChange("defects", e.target.value)}
                  onBlur={(e) => onFieldBlur?.("defects", e.target.value)}
                  placeholder={LISTING_DEFECTS_PLACEHOLDER}
                  className="w-full p-2 border rounded-lg min-h-[120px]"
                  rows={4}
                />
                <FieldError message={errors.defects} />
              </div>
            </div>
            <DescriptionContactPolicyNotice />

            <div className="border-t border-border pt-6">
              <PickupScheduleFields
                pickupDays={pickupDays}
                pickupStartHour={formData.pickupStartHour}
                pickupEndHour={formData.pickupEndHour}
                onDayToggle={onDayToggle}
                onPickupStartHourChange={(v) =>
                  onFieldChange("pickupStartHour", v)
                }
                onPickupEndHourChange={(v) => onFieldChange("pickupEndHour", v)}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Content */}
      <FormStep isActive={true} stepNumber={currentStep}>
        {renderStepContent()}
      </FormStep>

      {/* Navigation */}
      <StepNavigation
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        onNext={handleNext}
        onPrevious={handlePrevious}
        canProceed={canProceed}
        isLastStep={currentStep === TOTAL_STEPS}
        onSubmit={() => {
          if (!surfaceFreeTextStepErrors()) return;
          onSubmit?.();
        }}
        isLoading={isLoading}
      />

      {/* Progress Bar */}
      <FormProgressBar
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        currentSection={getCurrentSection(currentStep)}
        sectionLabels={["Item Details", "Pricing & Media", "Additional Info"]}
      />
    </div>
  );
}

// Export helper to get current section
export function getCurrentSection(step: number): 1 | 2 | 3 {
  if (step <= 3) return 1;
  if (step <= 5) return 2;
  return 3;
}
